'use client';
import React from 'react';
import Link from '@/core/i18n/navigation';
import { Image as ImageIcon } from 'lucide-react';
import { cn } from '@/core/utils/cn';
import { getPublicMediaUrl } from '@/core/utils/media';
import { toCssAspectRatio } from '@/core/utils/aspectRatio';
import { BlockProps } from './types';

import { AnimatedWebpPlayer } from '@/core/components/Builder/shared/AnimatedWebpPlayer';
import { ResponsiveImage, IMAGE_QUALITY, lcpImagePriority, type ImageLoadPriority } from '@/core/components/Builder/shared/ResponsiveImage';
import { useProductState } from '@/core/components/Builder/ProductContext';
import { toAltText } from '@/core/utils/alt-text';
import { useImageSlot } from '../ImageSlotContext';
import { buildImageSizes, narrowSlot } from '../imageSlot';

interface ImageBlockProps extends BlockProps {
  setIsResizing: (id: string | null) => void;
  setResizeStartX: (x: number) => void;
  setResizeStartWidth: (w: number) => void;
}

export function ImageBlock({
  block,
  blockStyles,
  currentStyles,
  isAdmin,
  viewport,
  selectedBlockId,
  setIsResizing,
  setResizeStartX,
  setResizeStartWidth,
  isNested,
  isFirstBlock,
  firstBlockBreakpoints,
}: ImageBlockProps) {
  // Before the wrapper stopped rendering styles.backgroundImage as CSS for this block
  // type, the generic "Tło — obraz" picker (AppearanceEditor) was the only one many
  // merchants ever found, so existing pages can have an image saved there with
  // `content` still empty. Falling back to it here migrates those blocks back to
  // visible on read, instead of requiring every affected page to be re-edited by hand.
  const desktopContent = block.content || currentStyles.backgroundImage;
  const mobileContent = block.settings?.mobileImage;
  const fallbackContent = desktopContent || mobileContent;

  const activeContent = isAdmin
    ? (viewport === 'mobile' ? (mobileContent || desktopContent) : (desktopContent || mobileContent))
    : fallbackContent;

  // `data:` URIs must pass through untouched — getPublicMediaUrl only handles
  // http(s)/relative storage paths and would otherwise mangle an inline preview.
  const resolveUrl = (src: string | undefined) => (src?.startsWith('data:') ? src : getPublicMediaUrl(src));
  const hasDistinctMobile = !isAdmin && !!mobileContent && !!desktopContent && mobileContent !== desktopContent;
  const linkUrl = block.settings?.link || block.settings?.href || block.settings?.cta_url;

  // The old fallback "Obraz" told Google and screen readers nothing. On a product page an unlabeled
  // image is almost always a photo of that product; anywhere else an empty alt at least marks it
  // decorative instead of pretending to describe it.
  const productState = useProductState();
  const imageAlt = block.settings?.decorative === true ? '' : toAltText(block.settings?.alt) || toAltText(productState?.product?.name) || '';
  const hasLink = Boolean(linkUrl || block.settings?.useLink);

  const activeAspectRatio = toCssAspectRatio(currentStyles.aspectRatio || (blockStyles as any).aspectRatio);
  const hasConstrainedSize = (activeAspectRatio && activeAspectRatio !== 'auto') || !!currentStyles.height;

  const imageStyles: React.CSSProperties = {
    objectFit: hasConstrainedSize ? (currentStyles.objectFit as any || 'cover') : undefined,
    objectPosition: currentStyles.objectPosition || 'center center',
  };

  const imageClass = cn(
    'builder-image-zoom',
    hasConstrainedSize ? 'absolute inset-0 w-full h-full' : 'w-full h-auto max-h-full block'
  );

  // Optional "frame" background behind the content image — letterboxing shown
  // where the picture doesn't fill the box (e.g. object-fit: contain). This is
  // a dedicated settings key, deliberately NOT the generic styles.backgroundImage
  // (which this block type ignores — see blockHelpers.ts) so it can never
  // collide with the legacy content-fallback value some older pages still carry.
  const frameBgImageUrl = block.settings?.frameBgImage ? getPublicMediaUrl(block.settings.frameBgImage) : undefined;

  const containerStyles: React.CSSProperties = {
    aspectRatio: activeAspectRatio,
    height: (activeAspectRatio && activeAspectRatio !== 'auto')
      ? 'auto'
      : (currentStyles.height ? `${currentStyles.height}px` : undefined),
    // `blockStyles.borderRadius` is already a complete CSS value (e.g. "24px")
    // from computeBlockStyles' formatStyle() — appending another "px" here
    // used to produce an invalid value like "24pxpx", which browsers silently
    // ignore, so the radius always rendered as 0 regardless of what was set.
    borderRadius: (blockStyles as any).borderRadius || '0px',
    // borderWidth/borderColor aren't part of computeBlockStyles' output, so
    // read them straight from currentStyles instead of the (always-empty)
    // blockStyles fields used here before.
    borderWidth: `${currentStyles.borderWidth || 0}px`,
    borderColor: currentStyles.borderColor || 'transparent',
    borderStyle: currentStyles.borderWidth ? 'solid' : 'none',
    backgroundColor: block.settings?.frameBgColor || (blockStyles as any).backgroundColor || 'transparent',
    backgroundImage: frameBgImageUrl ? `url(${frameBgImageUrl})` : undefined,
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  const isFullWidth = currentStyles.fullWidth === true;

  const containerClass = cn(
    // transition-all only in the editor: on the live page it animated a hydration-time change of
    // height/aspect ratio over several frames, each one a separate layout shift.
    'w-full flex flex-col items-center justify-center group/image relative overflow-hidden',
    isAdmin && 'transition-all',
    !desktopContent && !mobileContent && 'bg-gray-50 border border-dashed border-gray-200',
    hasConstrainedSize ? 'h-auto' : 'h-full min-h-[100px]'
  );

  // The manual "priority" toggle still promotes a hero that isn't literally the page's first block
  // (e.g. a thin announcement bar sits above it); otherwise BlockRenderer decides — 'deferred' when
  // this block is hidden on some breakpoint, so its image isn't downloaded where it's never shown.
  const lcp: { priority: ImageLoadPriority; preloadMedia?: string } = block.settings?.priority === true
    ? { priority: 'eager' }
    : lcpImagePriority(isFirstBlock, firstBlockBreakpoints);
  const quality = block.settings?.imageQuality === 'high' ? IMAGE_QUALITY.high : IMAGE_QUALITY.standard;
  // The rendered width, not the viewport's: the column this image sits in (row context) times
  // the block's own width % per breakpoint. A flat "100vw / 75vw" made the browser download
  // 750px files for images shown ~400px (and less) wide in 2–3 column rows.
  const imageSlot = useImageSlot();
  const ownWidth = (viewportKey: 'mobile' | 'tablet' | 'desktop') => {
    const merged = { ...(block.styles || {}), ...(block.styles?.[viewportKey] || {}) };
    if (merged.fullWidth === true) return 1;
    const percent = Number(merged.width);
    return Number.isFinite(percent) && percent > 0 ? percent / 100 : 1;
  };
  const sizes = buildImageSizes(
    narrowSlot(imageSlot, { mobile: ownWidth('mobile'), tablet: ownWidth('tablet'), desktop: ownWidth('desktop') }),
    isFullWidth,
  );

  // The frame-by-frame player is only for merchants who changed playback (speed, frame time, end
  // pause, pause on hover). A plain animated WebP/GIF animates natively in <img>, and the optimizer
  // passes animated files through untouched — routing every .webp through the player made each
  // ordinary photo download twice (the <img> plus the player's fetch()) at full original size.
  const hasCustomPlayback =
    (block.settings?.animationSpeed !== undefined && block.settings.animationSpeed !== 1) ||
    !!block.settings?.frameDurationMs ||
    !!block.settings?.endPauseMs ||
    block.settings?.pauseOnHover === true;

  const renderImage = () => {
    if (!desktopContent && !mobileContent) {
      return <ImageIcon className="w-12 h-12 text-gray-200 group-hover/image:scale-110 transition-transform" />;
    }
    // Admin previews the simulated viewport's variant only; the storefront lets the real
    // viewport pick between desktop and mobile variants (same 640px breakpoint as the Slider).
    const primary = isAdmin ? activeContent : (desktopContent || mobileContent);
    const responsive = (src: string, mobileSrc?: string, extraClass?: string) => (
      <ResponsiveImage
        src={src}
        mobileSrc={mobileSrc}
        alt={imageAlt}
        sizes={sizes}
        quality={quality}
        priority={lcp.priority}
        preloadMedia={lcp.preloadMedia}
        className={cn(imageClass, extraClass)}
        style={imageStyles}
      />
    );
    if (!hasCustomPlayback) return responsive(primary, hasDistinctMobile ? mobileContent : undefined);

    const player = (src: string, extraClass?: string) => (
      <AnimatedWebpPlayer
        src={resolveUrl(src)}
        alt={imageAlt}
        speed={block.settings?.animationSpeed}
        frameDurationMs={block.settings?.frameDurationMs}
        endPauseMs={block.settings?.endPauseMs}
        pauseOnHover={block.settings?.pauseOnHover}
        style={imageStyles}
        className={cn(imageClass, extraClass)}
        fallbackElement={responsive(src, undefined, extraClass)}
      />
    );
    // A canvas can't be art-directed like <picture>, so custom playback with a separate mobile file
    // keeps the CSS-toggled pair (same 640px breakpoint as the Slider).
    if (hasDistinctMobile) {
      return (
        <>
          {player(desktopContent, 'hidden sm:block')}
          {player(mobileContent, 'block sm:hidden')}
        </>
      );
    }
    return player(primary);
  };

  const verticalAlign = currentStyles.verticalAlign || 'top';

  return (
    <div
      className={cn(
        'w-full flex',
        isAdmin && 'transition-all',
        isNested && 'h-full',
        blockStyles.textAlign === 'left'
          ? 'justify-start'
          : blockStyles.textAlign === 'right'
            ? 'justify-end'
            : 'justify-center',
        verticalAlign === 'center'
          ? 'items-center'
          : verticalAlign === 'bottom'
            ? 'items-end'
            : 'items-start'
      )}
    >
      <div
        className="relative group/resize"
        style={{
          width: isFullWidth ? '100%' : ((blockStyles as any).width ? `${(blockStyles as any).width}%` : '100%'),
          minWidth: '50px',
        }}
      >
        {isAdmin && selectedBlockId === block.id && (
          <div
            className="absolute -right-1 -bottom-1 w-4 h-4 bg-blue-600 rounded-full border-2 border-white cursor-se-resize z-50 shadow-md flex items-center justify-center hover:scale-125 transition-transform"
            onMouseDown={(e) => {
              e.stopPropagation();
              e.preventDefault();
              setIsResizing(block.id);
              setResizeStartX(e.clientX);
              setResizeStartWidth(Number((blockStyles as any).width || 100));
            }}
          >
            <div className="w-1.5 h-1.5 border-r border-b border-white rotate-45" />
          </div>
        )}
        
        {hasLink ? (
          <Link
            href={linkUrl || '#'}
            target={block.settings?.target || block.settings?.linkTarget || '_self'}
            rel={(block.settings?.target === '_blank' || block.settings?.linkTarget === '_blank') ? 'noopener noreferrer' : undefined}
            className="w-full block"
          >
            <div style={containerStyles} className={containerClass}>
              {renderImage()}
            </div>
          </Link>
        ) : (
          <div style={containerStyles} className={containerClass}>
            {renderImage()}
          </div>
        )}
      </div>
    </div>
  );
}
